import React from 'react';
import { STATUS_COLORS } from '../utils/mapIcons';

/**
 * @file MapSidebar.jsx
 * @description Sidebar for filtering projects on the map by status and search.
 */

/**
 * Sidebar component for project selection.
 * 
 * @param {Object} props
 * @param {boolean} props.isSidebarOpen - Sidebar visibility
 * @param {Function} props.setIsSidebarOpen - Visibility setter
 * @param {string} props.projectSearch - Current search term
 * @param {Function} props.setProjectSearch - Search setter
 * @param {Array} props.selectedProjectIds - List of selected project IDs
 * @param {Object} props.groupedProjects - Projects grouped by status
 * @param {Object} props.expandedGroups - Visibility state of status groups
 * @param {Function} props.toggleGroup - Toggle group expansion
 * @param {Function} props.toggleGroupProjects - Toggle all projects in a group
 * @param {Function} props.toggleProject - Toggle a single project
 * @param {Function} props.selectAll - Select all projects
 * @param {Function} props.deselectAll - Deselect all projects
 * @param {number} props.totalGpsCount - Total count of projects with GPS data
 */
export const MapSidebar = ({
    isSidebarOpen,
    setIsSidebarOpen,
    projectSearch,
    setProjectSearch,
    selectedProjectIds,
    groupedProjects,
    expandedGroups,
    toggleGroup,
    toggleGroupProjects,
    toggleProject,
    selectAll,
    deselectAll,
    totalGpsCount
}) => {
    return (
        <div className={`map-sidebar ${!isSidebarOpen ? 'collapsed' : ''}`}>
            <div className="map-sidebar-header">
                <h3>
                    <span>Projekte filtern</span>
                    <span className="map-sidebar-close" onClick={() => setIsSidebarOpen(false)}>✕</span>
                </h3>
                <input
                    className="map-sidebar-search"
                    placeholder="Projekt suchen..."
                    value={projectSearch}
                    onChange={e => setProjectSearch(e.target.value)}
                />
                <div className="map-sidebar-actions">
                    <button onClick={selectAll}>Alle anwählen</button>
                    <button onClick={deselectAll}>Alle abwählen</button>
                </div>
            </div>
            <div className="map-project-list">
                {Object.entries(groupedProjects).map(([status, projectsInGroup]) => {
                    if (projectsInGroup.length === 0) return null;
                    
                    const groupIds = projectsInGroup.map(p => p.id);
                    const allSelected = groupIds.every(id => selectedProjectIds.includes(id));
                    const someSelected = groupIds.some(id => selectedProjectIds.includes(id)) && !allSelected;
                    
                    const statusLabel = status === 'active' ? 'Aktiv' :
                                        status === 'paused' ? 'Inaktiv' :
                                        status === 'completed' ? 'Abgeschlossen' : 'Archiv';
                    
                    const isExpanded = expandedGroups[status];
                    
                    return (
                        <div key={status} className="map-project-group" style={{ marginBottom: '10px' }}>
                            <div 
                                className="map-project-group-header" 
                                style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between', 
                                    padding: '10px 12px', 
                                    background: '#f8fafc', 
                                    borderBottom: '1px solid #e2e8f0',
                                    borderTop: '1px solid #e2e8f0'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flex: 1 }} onClick={() => toggleGroup(status)}>
                                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{isExpanded ? '▼' : '▶'}</span>
                                    <span style={{ fontWeight: '600', fontSize: '0.9rem', color: '#334155' }}>{statusLabel} ({projectsInGroup.length})</span>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    ref={el => {
                                        if (el) el.indeterminate = someSelected;
                                    }}
                                    onChange={() => toggleGroupProjects(status, projectsInGroup)}
                                    style={{ cursor: 'pointer' }}
                                />
                            </div>
                            {isExpanded && (
                                <div className="map-project-group-items">
                                    {projectsInGroup.map(p => (
                                        <div
                                            key={p.id}
                                            className={`map-project-item ${selectedProjectIds.includes(p.id) ? 'selected' : ''}`}
                                            onClick={() => toggleProject(p.id)}
                                            style={{ paddingLeft: '24px' }}
                                        >
                                            <input
                                                type="checkbox"
                                                className="map-project-checkbox"
                                                checked={selectedProjectIds.includes(p.id)}
                                                readOnly
                                            />
                                            <div className="map-project-info">
                                                <div className="map-project-number">
                                                    <span className={`status-dot ${p.status}`} style={{ width: '6px', height: '6px', backgroundColor: STATUS_COLORS[p.status] }}></span>
                                                    {p.project_number}
                                                </div>
                                                <div className="map-project-name">{p.name}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
